import React, { useState, useEffect, useCallback } from 'react';
import { Newspaper, RefreshCw, ExternalLink, TrendingUp, TrendingDown, Minus, Megaphone, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sentimentStyle = (s) => {
  if (s === 'Positive') return { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: TrendingUp };
  if (s === 'Negative') return { cls: 'bg-rose-100 text-rose-700 border-rose-200', Icon: TrendingDown };
  return { cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: Minus };
};

const NewsItem = ({ item }) => {
  const { cls, Icon } = sentimentStyle(item.sentiment);
  return (
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-2 p-2 rounded-md hover:bg-slate-50 transition-colors group"
      data-testid="news-item"
    >
      <span className={`shrink-0 mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cls}`}>
        <Icon className="h-3 w-3" /> {item.sentiment}
      </span>
      <span className="flex-1 text-sm text-slate-700 leading-snug group-hover:text-slate-900">
        {item.title}
        <ExternalLink className="inline h-3 w-3 ml-1 text-slate-400 align-baseline" />
        <span className="block text-[11px] text-slate-400 mt-0.5">{item.source}{item.reason ? ` · ${item.reason}` : ''}</span>
      </span>
    </a>
  );
};

const StockNews = ({ portfolioId }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ stocks: [], date: '' });
  const [error, setError] = useState('');

  const load = useCallback(async (refresh = false) => {
    try {
      refresh ? setRefreshing(true) : setLoading(true);
      setError('');
      const res = await axios.get(`${API}/news`, { params: { portfolioId: portfolioId || 'all', refresh } });
      setData(res.data);
    } catch (e) {
      setError('Could not load news right now. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [portfolioId]);

  useEffect(() => { load(false); }, [load]);

  return (
    <Card className="border-2 border-blue-200 shadow-xl bg-gradient-to-br from-white to-blue-50" data-testid="stock-news-report">
      <CardHeader className="border-b-2 border-blue-200 bg-blue-50">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <CardTitle className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Newspaper className="h-6 w-6 text-blue-600" /> Daily Stock News
            </CardTitle>
            <p className="text-sm text-slate-600 mt-1">
              Latest news &amp; BSE/NSE-related announcements for your holdings, auto-tagged Positive / Negative / Neutral by AI{data.date ? ` · ${data.date}` : ''}
            </p>
          </div>
          <Button onClick={() => load(true)} disabled={refreshing || loading} variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50" data-testid="refresh-news-btn">
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Refreshing...' : 'Refresh News'}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-6">
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500" data-testid="news-loading">
            <Loader2 className="h-8 w-8 animate-spin mb-3 text-blue-500" />
            <p>Fetching latest news &amp; running sentiment analysis…</p>
            <p className="text-xs mt-1">This can take up to a minute the first time each day.</p>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-12 text-rose-600" data-testid="news-error">{error}</div>
        )}

        {!loading && !error && data.stocks.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <p className="text-lg">No equity holdings to track news for</p>
            <p className="text-sm mt-1">Add equity/ETF holdings to see their daily news here.</p>
          </div>
        )}

        {!loading && !error && data.stocks.length > 0 && (
          <div className="space-y-6">
            {data.stocks.map((s) => (
              <div key={s.ticker} className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`news-stock-${s.ticker}`}>
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <div>
                    <span className="font-bold text-slate-900">{s.ticker}</span>
                    <span className="text-sm text-slate-500 ml-2">{s.shareName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-100 text-emerald-700">+{s.positive}</Badge>
                    <Badge className="bg-rose-100 text-rose-700">−{s.negative}</Badge>
                    <Badge className="bg-slate-100 text-slate-600">~{s.neutral}</Badge>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-2 p-2">
                  <div>
                    <div className="text-xs font-semibold text-slate-500 uppercase px-2 py-1 flex items-center gap-1"><Newspaper className="h-3 w-3" /> News</div>
                    {(s.news || []).length === 0 && <div className="text-sm text-slate-400 px-2 py-2">No recent news</div>}
                    {(s.news || []).map((it, i) => <NewsItem key={it.link || `n-${i}`} item={it} />)}
                  </div>
                  <div className="md:border-l md:border-slate-100">
                    <div className="text-xs font-semibold text-slate-500 uppercase px-2 py-1 flex items-center gap-1"><Megaphone className="h-3 w-3" /> Announcements / Filings</div>
                    {(s.announcements || []).length === 0 && <div className="text-sm text-slate-400 px-2 py-2">No recent announcements</div>}
                    {(s.announcements || []).map((it, i) => <NewsItem key={it.link || `a-${i}`} item={it} />)}
                  </div>
                </div>
              </div>
            ))}
            <p className="text-xs text-slate-400 pt-2">News aggregated via Google News (India). Sentiment is AI-generated and indicative — not investment advice.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StockNews;
